import type { Dispatch, MutableRefObject, ReactNode, SetStateAction } from "react";
import { api } from "../../lib/api";
import { getAuthToken } from "../../lib/auth";
import { Skeleton } from "../../components/ui";
import FavGalleryVisual from "../../components/chat/FavGalleryVisual";
import CalendarFavoriteCard from "../../components/calendar/CalendarFavoriteCard";
import FavMessageRow, { continuesGroup } from "../../components/chat/FavMessageRow";
import type { KanbanList, KanbanTag } from "../../types/kanban";
import type { Msg } from "../../types/chat";
import type { KanbanItemWithTags, BasicAttachment, PreviewItem, Channel } from "./chatTypes";
import type { useFavorites } from "./hooks/useFavorites";
import type { useDms } from "./hooks/useDms";

type DmDisplayName = ReturnType<typeof useDms>["getDmDisplayName"];

/** Inputs for the landing dashboard's favorite cards. Everything is read at render time; nothing here holds state. */
export type LandingFavoriteCardsArgs = {
  favorites: string[];
  setFavorites: Dispatch<SetStateAction<string[]>>;
  parseListFav: ReturnType<typeof useFavorites>["parseListFav"];
  voids: { id: string; name?: string }[];
  getDmDisplayName: DmDisplayName;
  currentVoidId: string;
  channels: Channel[];
  channelTypeById: Record<string, string>;
  kanbanByChan: Record<string, KanbanList[]>;
  setKanbanByChan: Dispatch<SetStateAction<Record<string, KanbanList[]>>>;
  reloadKanban: (fqid: string) => unknown;
  /** Clears the "already reloaded" mark so a Retry click can reload the board again. */
  forgetKanbanReload: (fqid: string) => void;
  msgsByKey: Record<string, Msg[]>;
  previewsByChan: Record<string, PreviewItem[]>;
  unread: Record<string, number>;
  refreshPreview: (fqChanId: string) => unknown;
  quickTextByFav: Record<string, string>;
  setQuick: (fid: string, v: string) => void;
  quickPickerFor: string | null;
  setQuickPickerFor: Dispatch<SetStateAction<string | null>>;
  addQuickEmoji: (fid: string, emoji: string) => void;
  quickSend: (fid: string) => unknown;
  quickAddKanbanCardInline: (fqid: string, listId: string, fid: string) => unknown;
  favEmojis: string[];
  favScrollRefs: MutableRefObject<Record<string, HTMLDivElement | null>>;
  showPeople: boolean;
  setChanSheetOpen: (open: boolean) => void;
  setUsersSheetOpen: (open: boolean) => void;
  switchVoid: (id: string) => void;
  switchChannel: (id: string) => void;
  k: (vId: string, cId: string) => string;
  fq: (vId: string, cId: string) => string;
  /** The signed-in user's id, so their own messages read "You" like they do in a channel. */
  meId?: string;
  /** Avatars by user id, from whatever the app already has loaded (me, friends, current space members). */
  avatarByUserId?: Record<string, string | null | undefined>;
  /** Turns message text into clickable links, the same way a channel does. */
  linkify?: (text: string, key: string) => ReactNode;
};

export function buildLandingFavoriteCards({
  favorites, setFavorites, parseListFav, voids, getDmDisplayName, currentVoidId, channels, channelTypeById,
  kanbanByChan, setKanbanByChan, reloadKanban, forgetKanbanReload, msgsByKey, previewsByChan, unread,
  refreshPreview, quickTextByFav, setQuick, quickPickerFor, setQuickPickerFor, addQuickEmoji, quickSend,
  quickAddKanbanCardInline, favEmojis, favScrollRefs, showPeople, setChanSheetOpen, setUsersSheetOpen,
  switchVoid, switchChannel, k, fq, meId, avatarByUserId, linkify,
}: LandingFavoriteCardsArgs): ReactNode[] {
  const landingShowFavorites = (() => { try { return localStorage.getItem('landing.showFavorites') !== '0'; } catch { return true; } })();
  const landingFavoriteLimit = (() => { try { const v = parseInt(localStorage.getItem('landing.maxFavorites') || '4', 10); return Math.max(1, Math.min(8, isNaN(v) ? 4 : v)); } catch { return 4; } })();
  const landingFavoriteCards: ReactNode[] = [];
  if (landingShowFavorites) {
    const limitedFavorites = favorites.slice(0, landingFavoriteLimit);
    for (const fid of limitedFavorites) {
      if (fid.startsWith('klist:')) {
        const parsed = parseListFav(fid);
        if (!parsed) continue;
        const { vId, cIdRaw, fqid, listId } = parsed;
        const cId = cIdRaw.includes(':') ? cIdRaw.split(':')[1] : cIdRaw;
        const v = voids.find(vv => vv.id === vId);
        const vName = vId.startsWith('dm_') ? getDmDisplayName(v, vId) : (v?.name || vId);
        const lists = kanbanByChan[fqid];
        const list = lists?.find(l => l.id === listId);
        const cMeta = vId === currentVoidId ? channels.find(c=>c.id===cId) : null;
        const cName = cMeta?.name || (cId === 'chat' && vId.startsWith('dm_') ? 'Direct Message' : cId);
        const listItems = list ? (list.items as KanbanItemWithTags[]) : [];
        const incomplete = listItems.filter(it => !it.done);
        const totalTasks = list?.items.length ?? 0;
        const completed = totalTasks - incomplete.length;
        const completionPct = totalTasks ? Math.round((completed / totalTasks) * 100) : (list ? 100 : 0);
        landingFavoriteCards.push(
          <div key={fid} className="relative flex h-full min-w-0 flex-col gap-3">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-xs text-emerald-300/80 break-words [overflow-wrap:anywhere]">Kanban / {vName}</p>
                <div className="text-lg font-medium text-fg break-words [overflow-wrap:anywhere]">{list?.name || 'List'}</div>
                <div className="text-sm text-fg-2 break-words [overflow-wrap:anywhere]">#{cName}</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="rounded-full border border-emerald-700/70 bg-emerald-900/20 px-3 py-1 text-xs font-medium text-emerald-200 hover:bg-emerald-800/30 transition"
                  onClick={()=>{ switchVoid(vId); const shortId = cId.includes(':') ? cId.split(':')[1] : cId; switchChannel(shortId); }}
                >
                  Open board
                </button>
                <button className="rounded-full border border-transparent p-1 text-fg-3 hover:text-fg opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100" title="Remove favorite" aria-label="Remove favorite" onClick={()=>setFavorites(prev=>prev.filter(x=>x!==fid))}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                    <path d="m18 6-12 12"/>
                    <path d="m6 6 12 12"/>
                  </svg>
                </button>
              </div>
            </div>
            <div className="flex items-center gap-3 text-sm text-fg-2 flex-wrap">
              <div className="text-emerald-300 font-medium">{Math.max(incomplete.length, 0)} open</div>
              <div className="h-1.5 flex-1 min-w-[120px] rounded-full bg-white/10 overflow-hidden">
                <span className="block h-full bg-emerald-400" style={{ width: `${Math.min(100, Math.max(0, completionPct))}%` }} />
              </div>
              <div className="text-xs text-fg-3 break-words [overflow-wrap:anywhere]">{totalTasks} tasks</div>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); quickAddKanbanCardInline(fqid, listId, fid); }}>
              <input
                className="w-full surface-inset rounded-full border border-transparent px-4 py-2 text-sm text-fg placeholder:text-fg-3 outline-none focus:border-emerald-500/60"
                placeholder="+ Add a card…"
                aria-label="Add a card"
                enterKeyHint="done"
                value={quickTextByFav[fid] || ''}
                onChange={(e) => setQuick(fid, (e.target as HTMLInputElement).value)}
                autoComplete="off"
              />
              <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
            </form>
            <div ref={(el)=>{ favScrollRefs.current[fid] = el; }} className="space-y-2 max-h-48 overflow-y-auto pr-1 text-sm">
              {lists === undefined ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-4 w-3/5" />
                </div>
              ) : (!list && (lists?.length || 0) === 0) ? (
                <div className="flex items-center justify-between gap-2 text-fg-3">
                  <span>Open this kanban once to load lists.</span>
                  <button className="text-xs px-2 py-1 rounded border border-white/10 text-fg-2 hover:bg-white/10" onClick={()=>{ forgetKanbanReload(fqid); reloadKanban(fqid); }}>Retry</button>
                </div>
              ) : !list ? (
                <div className="text-fg-3">List not found or removed</div>
              ) : incomplete.length === 0 ? (
                <div className="text-fg-3">No incomplete tasks</div>
            ) : (
              incomplete.map(it => (
                <label key={it.id} className="flex items-start gap-3 surface-inset rounded-xl px-3 py-2 hover:bg-white/5 transition-colors">
                    <input type="checkbox" className="mt-1 accent-emerald-500" checked={false} onChange={async(e)=> {
                      try { const tok=getAuthToken()||''; await api.patchAuth('/kanban/items',{ itemId: it.id, done: e.target.checked }, tok); } catch { /* noop */ }
                      setKanbanByChan(old => {
                        const all = (old[fqid]||[]).map(l=>({ ...l, items: [...l.items] }));
                        const target = all.find(l=>l.id===listId); if(!target) return old;
                        const idx = target.items.findIndex(x=>x.id===it.id); if(idx!==-1){ target.items[idx] = { ...target.items[idx], done: true }; }
                        return { ...old, [fqid]: all };
                      });
                    }} />
                    <div className="min-w-0 flex-1">
                      <div className="text-fg whitespace-pre-wrap break-words">{it.content}</div>
                      {it.tags && it.tags.length ? (
                        <div className="mt-1 flex flex-wrap gap-1 text-meta text-fg-2">
                          {it.tags.map((tag: KanbanTag) => (
                            <span key={tag.id} className="rounded-full border border-white/10 px-2 py-0.5">{tag.label ?? tag.id}</span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>
        );
        continue;
      }
      const [vId, cId] = fid.split(':');
      const v = voids.find(vv => vv.id === vId);
      const vName = vId.startsWith('dm_') ? getDmDisplayName(v, vId) : (v?.name || vId);
      const cMeta = vId === currentVoidId ? channels.find(c=>c.id===cId) : null;
      const cName = cMeta?.name || (cId === 'chat' && vId.startsWith('dm_') ? 'Direct Message' : cId);
      const kk = k(vId, cId);
      const mbase: PreviewItem[] = (msgsByKey[kk] || []).map((m): PreviewItem => ({
        id: m.id,
        content: m.content,
        authorName: m.authorName,
        authorId: m.authorId,
        authorColor: m.authorColor,
        createdAt: m.createdAt || '',
        attachments: Array.isArray(m.attachments) ? (m.attachments as BasicAttachment[]) : [],
      }));
      const pbase: PreviewItem[] = previewsByChan[`${vId}:${cId}`] || [];
      const seen: Record<string, boolean> = {};
      const merged: PreviewItem[] = ([] as PreviewItem[])
        .concat(mbase, pbase)
        .filter(it => { if (!it.id || seen[it.id]) return false; seen[it.id] = true; return true; })
        .sort((a,b) => (new Date(a.createdAt||0).getTime()) - (new Date(b.createdAt||0).getTime()));
      const items = merged.slice(-15);
      const unreadCount = unread[`${vId}:${cId}`] || 0;
      const fqid = fq(vId, cId);
      const ctypeKnown = channelTypeById[fqid] || (vId === currentVoidId ? channels.find(c=>c.id===cId)?.type : undefined);
      const landingQuick = (()=>{ try { return localStorage.getItem('landing.quickComposer') !== '0'; } catch { return true; } })();
      const hideComposer = (ctypeKnown === 'kanban' || ctypeKnown === 'form' || ctypeKnown === 'habit' || ctypeKnown === 'calendar' || ctypeKnown === 'challenge' || !!kanbanByChan[fqid]) || !landingQuick;
      if (ctypeKnown === 'calendar') {
        landingFavoriteCards.push(
          <CalendarFavoriteCard
            key={fid}
            fid={fqid}
            spaceName={vName}
            channelName={cName}
            onOpen={() => { switchVoid(vId); const shortId = cId.includes(':') ? cId.split(':')[1] : cId; switchChannel(shortId); }}
            onRemove={() => setFavorites(prev => prev.filter(x => x !== fid))}
          />
        );
        continue;
      }
      if (ctypeKnown === 'gallery') {
        const rotateCount = (()=>{ try { const v=parseInt(localStorage.getItem('fav.gallery.rotateCount')||'5',10); return Math.max(1, Math.min(12, isNaN(v)?5:v)); } catch { return 5; } })();
        const frames: { url: string; name?: string }[] = [];
        for (let i = merged.length - 1; i >= 0 && frames.length < rotateCount; i--) {
          const atts = merged[i].attachments || [];
          const img = atts.find((a: BasicAttachment)=>{ const t=String(a?.contentType||'').toLowerCase(); const u=String(a?.url||'').toLowerCase(); return t.startsWith('image/') || /\.(png|jpe?g|webp)(\?.*)?$/.test(u); });
          if (img) frames.push({ url: img.url || '', name: img.name });
        }
        const galFit = (()=>{ try { const v = localStorage.getItem('fav.gallery.fit'); return v === 'cover' ? 'cover' : 'contain-blur'; } catch { return 'contain-blur'; } })();
        const galHover = (()=>{ try { const v = localStorage.getItem('fav.gallery.hover'); return v === 'none' ? 'none' : 'subtle'; } catch { return 'subtle'; } })();
        const rotate = (()=>{ try { return localStorage.getItem('fav.gallery.rotate') !== '0'; } catch { return true; } })();
        const rotateSeconds = (()=>{ try { const v=parseInt(localStorage.getItem('fav.gallery.rotateSeconds')||'8',10); return Math.max(3, Math.min(60, isNaN(v)?8:v)); } catch { return 8; } })();
        const rotatePause = (()=>{ try { return localStorage.getItem('fav.gallery.rotatePause') !== '0'; } catch { return true; } })();
        const galTransition = (()=>{ try { const v=localStorage.getItem('fav.gallery.transition')||'fade'; return v==='snap' ? 'snap' : 'fade'; } catch { return 'fade'; } })();
        landingFavoriteCards.push(
          <div key={fid} className="relative flex h-full min-w-0 flex-col">
            <div className="relative flex flex-col h-full">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs text-emerald-200/80">Gallery / {vName}</p>
                  <div className="text-lg font-medium text-white">#{cName}</div>
                </div>
                <button className="rounded-full border border-transparent p-1 text-fg-2 hover:text-white opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100" title="Remove favorite" aria-label="Remove favorite" onClick={()=>setFavorites(prev=>prev.filter(x=>x!==fid))}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                    <path d="m18 6-12 12"/>
                    <path d="m6 6 12 12"/>
                  </svg>
                </button>
              </div>
              <button className="group relative flex-1" onClick={()=>{ switchVoid(vId); const shortId = cId.includes(':') ? cId.split(':')[1] : cId; switchChannel(shortId); }}>
                <div className={`relative mt-3 w-auto ${galFit === 'contain-blur' ? 'pt-[100%]' : 'pt-[66%]'} rounded-2xl border border-white/10 overflow-hidden`}>
                  <FavGalleryVisual
                    frames={frames}
                    fit={galFit}
                    hover={galHover}
                    rotate={rotate}
                    seconds={rotateSeconds}
                    pause={rotatePause}
                    transition={galTransition}
                    showPeopleHandle={showPeople}
                      onOpenChannels={() => { if (String(currentVoidId || '').startsWith('dm_')) return; setChanSheetOpen(true); setUsersSheetOpen(false); }}
                    onOpenPeople={() => { setUsersSheetOpen(true); setChanSheetOpen(false); }}
                  />
                </div>
              </button>
            </div>
          </div>
        );
        continue;
      }
      const lastMessageAuthor = items[items.length - 1]?.authorName || 'Someone';
      const previewItems = items.slice(-5);
      const isDm = vId.startsWith('dm_');
      const previewLoading = previewsByChan[`${vId}:${cId}`] === undefined && !msgsByKey[kk];
      const openChannel = () => { switchVoid(vId); const shortId = cId.includes(':') ? cId.split(':')[1] : cId; switchChannel(shortId); };
      landingFavoriteCards.push(
        <div key={fid} className="flex h-full min-w-0 flex-col gap-3">
          <div className="flex items-start justify-between gap-3">
            <button type="button" className="min-w-0 text-left" onClick={openChannel} title="Open channel">
              <p className="text-xs text-fg-3 truncate">{isDm ? 'Direct message' : vName}</p>
              <div className="flex items-center gap-2 text-lg font-medium text-fg">
                <span className="truncate">{isDm ? vName : `#${cName}`}</span>
                {unreadCount > 0 && <span className="shrink-0 rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs font-medium text-emerald-200">{unreadCount} new</span>}
              </div>
              <p className="text-sm text-fg-2 truncate">Latest from {lastMessageAuthor}</p>
            </button>
            <div className="flex shrink-0 items-center gap-1">
              <button className="rounded-full border border-transparent p-1 text-fg-3 hover:text-fg" title="Refresh preview" aria-label="Refresh preview" onClick={()=>refreshPreview(`${vId}:${cId}`)}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                  <path d="M21 12a9 9 0 1 1-3-6.7"/>
                  <path d="M21 3v6h-6"/>
                </svg>
              </button>
              <button className="rounded-full border border-transparent p-1 text-fg-3 hover:text-fg opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100" title="Remove favorite" aria-label="Remove favorite" onClick={()=>setFavorites(prev=>prev.filter(x=>x!==fid))}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                  <path d="m18 6-12 12"/>
                  <path d="m6 6 12 12"/>
                </svg>
              </button>
            </div>
          </div>
          <div
            ref={(el)=>{
              favScrollRefs.current[fid] = el;
              try { if (el) el.scrollTop = el.scrollHeight; } catch {}
            }}
            className="min-w-0 flex-1 cursor-pointer overflow-y-auto pr-1 text-sm"
            onClick={(e) => { if (!(e.target as HTMLElement).closest('a,button')) openChannel(); }}
          >
            {previewLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-3/5" />
              </div>
            ) : previewItems.length === 0 ? (
              <div className="surface-inset rounded-xl px-3 py-2 text-fg-3">Start the conversation to see a preview.</div>
            ) : (
              previewItems.map((it, i) => {
                const atts = it.attachments || [];
                const hasImg = atts.some((a: BasicAttachment)=>{ const t=String(a?.contentType||'').toLowerCase(); const u=String(a?.url||'').toLowerCase(); return t.startsWith('image/') || /\.(png|jpe?g|webp)(\?.*)?$/.test(u); });
                const text = String(it.content || '').trim();
                const onlyImage = hasImg && text === '';
                const prev = i > 0 ? previewItems[i - 1] : null;
                return (
                  <FavMessageRow
                    key={it.id}
                    msg={it}
                    grouped={continuesGroup(it, prev)}
                    isMine={!!meId && !!it.authorId && it.authorId === meId}
                    avatarUrl={it.authorId ? avatarByUserId?.[it.authorId] : undefined}
                  >
                    {onlyImage ? <span className="text-fg-2">Shared an image</span> : (linkify ? linkify(it.content || '', `fav-${fid}-${it.id}`) : (it.content || ''))}
                  </FavMessageRow>
                );
              })
            )}
          </div>
          {!hideComposer && (
            <div className="mt-1">
              <div className="flex items-center gap-2 surface-inset rounded-full px-5 py-1">
                <form className="flex min-w-0 flex-1" onSubmit={(e) => { e.preventDefault(); quickSend(fid); }}>
                  <input
                    className="min-w-0 flex-1 bg-transparent py-2 text-sm text-fg placeholder:text-fg-3 outline-none text-left leading-6"
                    placeholder={isDm ? `Message ${vName}` : `Message #${cName}`}
                    aria-label={isDm ? `Message ${vName}` : `Message #${cName}`}
                    enterKeyHint="send"
                    value={quickTextByFav[fid] || ''}
                    onChange={(e) => setQuick(fid, (e.target as HTMLInputElement).value)}
                    autoComplete="off"
                  />
                  <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
                </form>
                <button
                  type="button"
                  className="h-9 w-9 rounded-full text-fg-2 hover:text-fg hover:bg-white/10 flex items-center justify-center"
                  title="Emoji" aria-label="Emoji"
                  onClick={() => setQuickPickerFor(quickPickerFor===fid?null:fid)}
                >
                  <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' className='h-5 w-5'>
                    <circle cx='12' cy='12' r='9'/>
                    <path d='M8 14s1.5 2 4 2 4-2 4-2'/>
                    <path d='M9 9h.01M15 9h.01'/>
                  </svg>
                </button>
                <button
                  type="button"
                  className="h-9 w-9 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center disabled:opacity-40"
                  onClick={() => quickSend(fid)}
                  disabled={!((quickTextByFav[fid]||'').trim())}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                    <path d="M22 2L11 13"/>
                    <path d="M22 2l-7 20-4-9-9-4 20-7z"/>
                  </svg>
                </button>
              </div>
              {quickPickerFor === fid && (
                <div className="mt-2 flex flex-wrap gap-1 surface-inset rounded-2xl px-3 py-2">
                  {favEmojis.map((emoji) => (
                    <button key={`${fid}-${emoji}`} className="px-2 py-1 text-lg text-fg hover:bg-white/10 rounded" onClick={()=>addQuickEmoji(fid, emoji)}>
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
  }
  return landingFavoriteCards;
}
